import {
  isEmpty,
  merge,
  find,
  pick,
  once,
} from 'lodash-es'
import RiotControl from 'riotcontrol'
import logger from '../logger'
import AddressUtils from './address-utils'
import { parse } from 'libphonenumber-js'
import routesDef from '../routes-def'
import RouteRedirect from '../route-redirect'
import { OrderIssues } from './error-utils'
import { EmailRegex } from './email-utils'
import CCUtils from './credit-card-utils'
import appProps from '../app-props'

const CreditCardUtils = new CCUtils()

const useByEads = appProps.get('checkout.audit.use.by.ead', false)

import { scrubAddress } from '../observables/application-api-observable'
import { BillingAddressInstance } from '../observables/billing-address'
import { LineItemsInstance } from '../observables/line-items'
import { TendersInstance } from '../observables/tenders'
import { OrderObservableInstance } from '../observables/order-observable'

const routes = Object.assign({}, routesDef)

/**
 * Bootstrap utilities class
 */
class BootstrapUtils {

  constructor(appModel) {
    /* eslint-disable global-require */
    
    /* eslint-enable global-require */

    // Give the bootstrap utility information
    // about the application model so it can
    // wait for events.
    this.appModel = appModel

    // Bind class instance methods to the class
    this.doBillingAddress = this.doBillingAddress.bind(this)
    this.doContactInfo = this.doContactInfo.bind(this)
    this.doShippingAddress = this.doShippingAddress.bind(this)
    this.doTenders = this.doTenders.bind(this)
    this.doAudit = this.doAudit.bind(this)
    this.checkForBillingAddress = this.checkForBillingAddress.bind(this)
    this.prepareBillingAddress = this.prepareBillingAddress.bind(this)
    this.applyBillingAddress = this.applyBillingAddress.bind(this)
    this.waitForCalcEads = this.waitForCalcEads.bind(this)
    this.waitForShipCharges = this.waitForShipCharges.bind(this)
    this.checkForContactInfo = this.checkForContactInfo.bind(this)
    this.checkForFulfillmentErrors = this.checkForFulfillmentErrors.bind(this)
    this.checkShippingReadiness = this.checkShippingReadiness.bind(this)
    this.checkForShippingAddress = this.checkForShippingAddress.bind(this)
    this.prepareShippingAddress = this.prepareShippingAddress.bind(this)
    this.applyShippingAddress = this.applyShippingAddress.bind(this)
    this.checkForTenders = this.checkForTenders.bind(this)
    this.applyTenders = this.applyTenders.bind(this)
    this.conditionalAudit = this.conditionalAudit.bind(this)
    this.wireCalcEadsEvents = this.wireCalcEadsEvents.bind(this)
    this.wireShippingChargesEvents = this.wireShippingChargesEvents.bind(this)
  }

  /**
   * Application Bootstrap main method. This method
   * analyzes the order and will accordingly attempt
   * to apply various information the user object contains
   * to the order. This allows the order to get to a state
   * where very little additional information is needed from
   * the user, and they can be fast-forwarded to a more advanced
   * page of checkout
   * @param  {Object} model The order object
   * @return {Promise -> String}       Returns a promise which will eventually return a
   *                                   final route determined by the bootstrap process
   */
  bootstrapApplication(model) {
    // Reference for throw statements.
    this.orderModel = model
    this.finalRoute = routes.orderSummary
    this.auditRequired = false
    this.isFinalAudit = false

    return Promise.resolve(model)
      .then(this.doBillingAddress)
      .then(this.doContactInfo)
      .then(this.doShippingAddress)
      .then(this.doTenders)
      .then((model) => {
        this.isFinalAudit = true
        return this.doAudit(model)
      })
      .then(() => this.finalRoute)
  }

  // ------------------------------------------------- //


  // //////////
  // Helpers //
  // //////////

  /**
   * Helper method to take care of the auto application of the users saved billing
   * address to the order
   */
  doBillingAddress(model) {
    return this.checkForBillingAddress(model)
      .then(this.waitForCalcEads)
      .then((nuModel) => {
        if (!useByEads) return this.waitForShipCharges(nuModel)
        return nuModel
      })
  }

  /**
   * Helper method to take care of the auto application of the users contact information
   * address to the order
   */
  doContactInfo(model) {
    return this.checkForContactInfo(model)
  }

  /**
   * Helper method to take care of the auto application of the users saved shipping
   * address to the order (or billing if there is no saved shipping address).
   */
  doShippingAddress(model) {
    return this.checkForFulfillmentErrors(model)
      .then(this.checkShippingReadiness)
  }

  /**
   * Helper method to take care of the auto application of the users saved
   * tenders to the order
   */
  doTenders(model) {
    return this.checkForTenders(model)
  }

  /**
   * Helper method to take care of the audit process
   * after all auto application has occurred.
   * tenders to the order
   */
  doAudit(model) {
    return this.conditionalAudit(model)
  }

  // ------------------------------------------------- //


  // //////////////////
  // Billing Address //
  // //////////////////

  // Return an immediately resolved promise
  // to support promise chain flattening
  checkForBillingAddress(model) {
    const billingAddress = model.safeGet('billingAddress')
    const currentBillingAddress = (billingAddress) || model.safeGet('user.savedBillingAddress')
    const isAddressValid = address => (address) && (AddressUtils.isScrubbed(address))

    // If no billing address on order and none available or if it's only a partial address
    if (!currentBillingAddress || (!isAddressValid(currentBillingAddress.address)
      && !AddressUtils.isReadyForScrubbing(currentBillingAddress.address))) {
      throw new RouteRedirect('user has invalid billing address', routes.contactBilling, this.orderModel)
    }

    // The address is good, resolve immediately
    if (billingAddress) return Promise.resolve(model)

    // Continue work
    return this.prepareBillingAddress(currentBillingAddress)
      .then(this.applyBillingAddress)
      .then(this.doAudit)
  }

  prepareBillingAddress(currentBillingAddress) {
    // If the address is not scrubbed but complete scrub it,
    // otherwise move on to the next step
    if (currentBillingAddress.address.scrubStatus === 'NOT_SCRUBBED'
      && AddressUtils.isReadyForScrubbing(currentBillingAddress.address)) {
      return scrubAddress(currentBillingAddress.address)
        .then(scrubbedBillingAddress => scrubbedBillingAddress)
        .catch((error) => {
          logger(`ERROR :: USPS Scrub failed: ${error}`, 'error')()
          logger(`${error}`, 'error')()
          throw new RouteRedirect('User has invalid billing address or bad scrub', routes.contactBilling, this.orderModel)
        })
    }

    // do nothing, return current billing
    // for the order billing address update.
    return currentBillingAddress
  }

  applyBillingAddress(billingAddress) {
    const isAddressValid = address => (address) && (AddressUtils.isScrubbed(address))

    // If the billing address is valid, apply it
    if (isAddressValid(billingAddress.address)) {
      return BillingAddressInstance.updateBillingAddress(billingAddress)
        .then(() => {
          this.auditRequired = true
          return this.orderModel
        })
        .catch((error) => {
          // Handle issues in the billing address update
          const svcerrs = error.responseJSON
          const aderrs = []
          const cterrs = []
          svcerrs.error.forEach((e) => {
            if (e.parameterName === 'phoneNumber' || e.parameterName === 'email') cterrs.push(e)
            else aderrs.push(e)
          })
          // address problems
          if (aderrs.length > 0 && cterrs.length === 0) {
            throw new RouteRedirect('user billing address has errors', routes.contactBilling, this.orderModel)
          } else if (aderrs.length === 0 && cterrs.length > 0) {
            // contact info problems
            throw new RouteRedirect('user contact info has errors', routes.contactBilling, this.orderModel)
          } else {
            // if we hit here we're in trouble
            logger('address and contact issues found', 'error')()
            throw new RouteRedirect('user billing AND contact have errors', routes.contactBilling, this.orderModel)
          }
        })
    }

    throw new RouteRedirect('user has invalid billing address', routes.contactBilling, this.orderModel)
  }

  waitForCalcEads(model) {
    // If there is a problem with the call to fulfillments
    // direct the user to the billing address page
    if (this.appModel.fulfillmentsFailure) {
      throw new RouteRedirect('Fulfillments call failed, redirecting to billing', routes.contactBilling, this.orderModel)
    }

    // Check to see if there is a model and if there
    // is a fetch in progress otherwise the app is not
    // fetching or going to fetch.
    if (isEmpty(this.orderModel.safeGet('getters.fulfillmentsModel')) || this.appModel.isFetchingFulfillments) {
      const fulfillmentsPromise = new Promise((resolve, reject) => {
        this.wireCalcEadsEvents(resolve, reject)
      })

      return fulfillmentsPromise
        .then(() => model)
    }

    // fulfillments model already present
    return model
  }

  waitForShipCharges(model) {
    // If there is a problem with the call to ship charges
    // direct the user to the billing address page
    if (this.appModel.shippingChargeFailure) {
      throw new RouteRedirect('Fulfillments call failed, redirecting to billing', routes.contactBilling, this.orderModel)
    }

    // Once the billing address is applied, the subscribers
    // listening for that will fire off requests to shipping
    // charges. This listens for those requests to finish before
    // moving on to the next section. This is primarily so that
    // when the shipping address is auto-applied, there are
    // shipping methods available for application.

    if (isEmpty(this.orderModel.safeGet('getters.shipChargesModel')) || this.appModel.isFetchingShippingCharge) {
      const shippingChargesPromise = new Promise((resolve, reject) => {
        this.wireShippingChargesEvents(resolve, reject)
      })

      return shippingChargesPromise
        .then(() => model)
    }

    // Shipping charges already present
    return model
  }

  // ------------------------------------------------- //


  // ///////////////
  // Contact Info //
  // ///////////////

  checkForContactInfo(model) {
    const email = model.safeGet('billingAddress.address.email')
    const phoneNumber = model.safeGet('billingAddress.address.phoneNumber')

    if (isEmpty(phoneNumber)) {
      throw new RouteRedirect('user is missing a phone number', routes.contactBilling, this.orderModel)
    }

    // eslint-disable-next-line no-control-regex
    const emailRegex = new RegExp(EmailRegex.source)
    const parsedPhone = parse(phoneNumber, model.safeGet('billingAddress.address.countryCode', 'US'))

    // email address is invalid
    if (!emailRegex.test(email) || isEmpty(parsedPhone)) {
      throw new RouteRedirect('user has invalid email or phone', routes.contactBilling, this.orderModel)
    }

    return Promise.resolve(model)
  }

  // ------------------------------------------------- //


  // ///////////////////
  // Shipping Address //
  // ///////////////////

  /**
   * Check to see if there are fulfillment restrictions
   * on the order before proceeding
   * @param  {Object} model Order model
   * @return {Object}       Order model
   */
  checkForFulfillmentErrors(model) {
    const orderIssues = new OrderIssues(this.orderModel.safeGet('orderIssues'))
    if (orderIssues.hasFulfillmentRestrictions()) {
      throw new RouteRedirect('user address has fulfillment restrictions', routes.shippingOptions, model)
    }

    return Promise.resolve(model)
  }

  /**
   * Check to see if the order has errors restricting
   * the application of a shipping address
   * @param  {Object} model Order model
   * @return {Object}       Order model
   */
  checkShippingReadiness(model) {
    const billingAddress = model.safeGet('billingAddress')
    const restrictions = model.safeGet('getters.fulfillmentsModel.hasAnyShippingRestrictions')
    const appliedAddress = model.safeGet('getters.getOrderShippingAddress')
    const giftItems = model.safeGet('getters.filteredPhysicalGiftLineItems', [])
    const shipItems = model.safeGet('getters.filteredShipLineItems', [])
    const shipMethods = model.safeGet('getters.filteredCommonShipMethods', [])
    const giftShipMethods = model.safeGet('getters.filteredPhysicalGiftShipMethods', [])

    // no addresses to apply, direct to shipping page
    if (!appliedAddress && !billingAddress) {
      throw new RouteRedirect('No address to apply as a shipping address', routes.shippingOptions, model)
    }

    // there are shipping restrictions
    if (restrictions) {
      throw new RouteRedirect('Shipping restrictions exist on the order', routes.shippingOptions, model)
    }

    // something bad happened, shipping items but no methods?
    if (shipItems && shipItems.length !== 0 &&
      (!shipMethods || shipMethods.length === 0)) {
      // Don't redirect for BY because we need the code to apply the address below to run
      // This scenario likely means it's out of stock and we need an address applied to the item
      // to trigger the out of stock modal via audit fulfillment data
      if (!useByEads) throw new RouteRedirect('No shipping methods available', routes.shippingOptions, model)
    }

    // something bad happened, shipping gifts but no methods?
    if (giftItems && giftItems.length !== 0 &&
      (!giftShipMethods || giftShipMethods.length === 0)) {
      throw new RouteRedirect('Gift items but no shipping methods available', routes.shippingOptions, model)
    }

    // If the items all already have a shipping address and ship method our work here is done
    if (model.safeGet('getters.isReadyForShipping') && !restrictions) {
      // If the items do not have an address/ship method use the billing address, fulfillment
      // and ship charges data is already present and valid from the billing address so there
      // is no need to call those services again

      return model
    }

    // Continue work
    return Promise.resolve(model)
      .then(this.checkForShippingAddress)
      .then(this.prepareShippingAddress)
      .then(this.applyShippingAddress)
  }

  checkForShippingAddress(model) {
    const billingAddress = model.safeGet('billingAddress')
    const appliedAddress = model.safeGet('getters.getOrderShippingAddress')
    const shipAddress = appliedAddress ||
        merge(billingAddress, { address: { storeId: null } })

    return shipAddress
  }

  prepareShippingAddress(address) {
    const payload = { shippingAddress: address }

    return payload
  }

  applyShippingAddress(address) {
    // Apply the already applied address to the order.
    return LineItemsInstance.updateLineItemsWithAddress(address)
      .catch((error) => {
        logger(`${error}`, 'error')()
        // This will catch all errors from this attempt
        throw new RouteRedirect('Error updating line items with shipping address or method information', routes.shippingOptions, this.orderModel)
      })
      .then(() => {
        this.auditRequired = true
        return this.orderModel
      })
  }

  // ------------------------------------------------- //


  // //////////
  // Tenders //
  // //////////

  checkForTenders(model) {
    const isApprovedForILA = model.safeGet('ilaTender.isInstantCreditApproved')
    const isQualifiedForILA = model.safeGet('ilaTender.instantCreditTempLimit') >= model.safeGet('balance')
    const savedTender = model.safeGet('getters.userSavedCreditCards', [])
    const first = find(savedTender, t => !CreditCardUtils.isCardExpired(t))
    let card

    // If ILA, pick that
    if (isApprovedForILA && isQualifiedForILA) {
      card = {
        ilaAppId: model.safeGet('ilaTender.instantCreditAppId'),
        type: 'CREDITCARD_REPOSITORY',
      }
    // Otherwise pick first unexpired card
    } else if (first) {
      card = pick(first, ['type', 'creditCardType', 'maskedNumber', 'number', 'expiration', 'pin', 'sequenceId'])
    // Else there is no available payment method
    } else {
      // If no tenders resolve so audit is called
      return Promise.resolve(model)
    }

    return this.applyTenders(card)
  }

  /**
   * This method is used to preset the tender information
   * on the order. It does not reach out to a BE service for
   * this work.
   * @return {Object} Order model
   */
  applyTenders(card) {
    // Store the tender and proceed
    return TendersInstance.storeCreditCard(card)
      .then(() => this.orderModel)
      .catch((error) => {
        logger(`${error}`, 'error')()
        throw new RouteRedirect('Error storing tender information', this.finalRoute, this.orderModel)
      })
  }

  // ------------------------------------------------- //


  // ///////
  // Misc //
  // ///////

  /**
   * Conditionally audits the application depending
   * on which page the user is on
   * @param  {Object} model Order model
   * @return {Model/Promise}       Either the model or a promise that eventually returns the model
   */
  conditionalAudit(model) {
    if (!this.auditRequired && this.finalRoute !== routes.orderSummary) return model
    // Don't trigger audit on the confirmation page and on the order summary page EADs need to be
    // fetched with the audit
    let options = {}
    if (model.externalOrderId) {
      if (this.isFinalAudit && this.finalRoute === routes.orderSummary) {
        options = { eadIncluded: true }
      }
    }

    return OrderObservableInstance.auditOrder(options)
      .then(() => {
        this.auditRequired = false
        return model
      })
  }

  /**
   * Wires up a conditional listener for the calculate
   * available shipping options call since later logic
   * is dependent on that succeeding. This seems like a
   * reasonable way to do this without re-writing the way
   * EAD's are already wired.
   * @param  {Function} resolve promise resolution to call when complete
   * @return {null}         no return
   */
  wireCalcEadsEvents(resolve, reject) {
    // Listen for the `fetchingFulfillmentsSuccess` event
    RiotControl.one('fetchingFulfillmentsSuccess', () => {
      resolve()
      this.unwireCalcEadsEvents()
    })

    // Listen for the `fetchingFulfillmentsFailure` event
    RiotControl.one('fetchingFulfillmentsFailure', once(() => {
      reject(new RouteRedirect('Error calculating available shipping options', routes.contactBilling, this.orderModel))
      this.unwireCalcEadsEvents()
    }))
  }

  /**
   * Un-wires the events previously wired in `wireShippingChargesEvents`
   * @return {none} No Return
   */
  unwireCalcEadsEvents() {
    RiotControl.off('fetchingFulfillmentsSuccess')
    RiotControl.off('fetchingFulfillmentsFailure')
  }

  /**
   * Wires up a conditional listener for the shipping
   * charges call since later logic is dependent on
   * that succeeding.
   * @param  {Function} resolve promise resolution to call when complete
   * @return {null}         no return
   */
  wireShippingChargesEvents(resolve, reject) {
    // Listen for the `shippingChargeSuccess` event
    RiotControl.one('shippingChargeSuccess', () => {
      resolve()
      this.unwireShippingChargesEvents()
    })

    // Listen for the `shippingChargeFailure` event
    // shippingCharge request failed so we probably have some sort of shipping restriction
    RiotControl.one('shippingChargeFailure', once(() => {
      // this.unwireShippingChargesEvents();
      const hasShipItems = this.orderModel.safeGet('getters.filteredShipLineItemsWithGC', []).length > 0
      if (!hasShipItems) {
        reject(new RouteRedirect('Error getting shipping charges, no ship items', routes.storePickup, this.orderModel))
      } else {
        reject(new RouteRedirect('Error getting shipping charges, ship items', routes.shippingOptions, this.orderModel))
      }

      this.unwireShippingChargesEvents()
    }))
  }

  /**
   * Un-wires the events previously wired in `wireShippingChargesEvents`
   * @return {none} No Return
   */
  unwireShippingChargesEvents() {
    RiotControl.off('shippingChargeSuccess')
    RiotControl.off('shippingChargeFailure')
  }
}

export default BootstrapUtils
